import { ThunkAction } from 'redux-thunk';

import { TRootState } from '@/redux/rootReducer';
import apiRequest from '@/services/apiRequest';
import { getCoreApi } from '@/utility/getCoreApi';
import { IAction } from '@/utility/redux/action';
import { getAuthToken, getSessionCookie } from '@/utility/session';

import type { TAuthFieldError } from './index';
import { fetchUser } from './userMini';

const UPDATE_USER_SERVICE_URL = `${getCoreApi()}/users/`;

const UPDATE_USER_PENDING = 'auth/UPDATE_USER_PENDING';
export const UPDATE_USER_FAILURE = 'auth/UPDATE_USER_FAILURE';
export const UPDATE_USER_SUCCESS = 'auth/UPDATE_USER_SUCCESS';

interface IUpdateUserPendingAction extends IAction {
  type: typeof UPDATE_USER_PENDING;
}

interface IUpdateUserSuccessAction extends IAction {
  type: typeof UPDATE_USER_SUCCESS;
}

/**
 * What apiRequest actually rejects with on a failed user update: the parsed
 * serror body, not an AxiosError. `code` is the distinguishing cause and
 * `errors[].field` names the offending input — serrors puts no code on the
 * nested entries, so both halves are needed to render a field-level message.
 */
interface IUpdateUserFailurePayload {
  code?: number | string;
  message?: string;
  errors?: TAuthFieldError[];
}

interface IUpdateUserFailureAction extends IAction {
  type: typeof UPDATE_USER_FAILURE;
  payload: IUpdateUserFailurePayload | undefined;
  error: boolean;
}

//#region actions for fetch user call (mini_me)
const updateUserPending = (): IUpdateUserPendingAction => ({ type: UPDATE_USER_PENDING });

const updateUserFailure = (error: IUpdateUserFailurePayload): IUpdateUserFailureAction => ({
  type: UPDATE_USER_FAILURE,
  payload: error,
  error: true,
});

// TODO: generate types from JSON response (payload not needed right now)
const updateUserSuccess = (): IUpdateUserSuccessAction => {
  return {
    type: UPDATE_USER_SUCCESS,
  };
};
//#endregion

export type IUpdateUserAction =
  | IUpdateUserPendingAction
  | IUpdateUserSuccessAction
  | IUpdateUserFailureAction;

// async FETCH USER THUNK (mini_me)
export const updateUser =
  (
    data: any,
    shouldSkipLoading?: boolean,
  ): ThunkAction<Promise<boolean>, TRootState, void, IUpdateUserAction> =>
  async dispatch => {
    if (!shouldSkipLoading) {
      await dispatch(updateUserPending());
    }
    // not using cookie values on redux here because it cannot be trusted as SSOT
    const authToken = getAuthToken();
    const session = getSessionCookie();

    const userId = session?.authenticated?.user_id;

    if (!authToken || !userId) {
      await dispatch(updateUserFailure({ code: 401 }));
      return false;
    }

    try {
      const user: any = await apiRequest(
        {
          url: UPDATE_USER_SERVICE_URL + userId,
          method: 'PATCH',
          data,
        },
        true,
      );

      if (user?.id) {
        await dispatch(fetchUser(shouldSkipLoading, true));
        dispatch(updateUserSuccess());
        return true;
      } else {
        dispatch(updateUserFailure({ code: 401 }));
        return false;
      }
    } catch (error) {
      // apiRequest rejects with the parsed serror body, so `code` and `errors`
      // are both already in hand here.
      dispatch(
        updateUserFailure({
          code: error?.code,
          message: error?.error,
          errors: error?.errors,
        }),
      );
      return false;
    }
  };
